/** Skeleton screen primitives for loading states */

interface SkeletonLineProps {
  width?: string
  height?: string
}

export function SkeletonLine({ width = 'w-3/4', height = 'h-3' }: SkeletonLineProps) {
  return (
    <div className={`${width} ${height} rounded skeleton-shimmer animate-shimmer`} />
  )
}

/** Skeleton that matches a CommentItem layout */
export function SkeletonComment() {
  return (
    <div className="px-3 py-2.5 border-l-[3px] border-l-neutral-200 dark:border-l-neutral-700 animate-fade-in">
      {/* Header: dot + name + time */}
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-2.5 h-2.5 rounded-full skeleton-shimmer animate-shimmer shrink-0" />
        <SkeletonLine width="w-20" height="h-2.5" />
        <span className="flex-1" />
        <SkeletonLine width="w-8" height="h-2" />
      </div>
      {/* Content lines */}
      <div className="space-y-1.5 ml-4">
        <SkeletonLine width="w-full" height="h-2.5" />
        <SkeletonLine width="w-2/3" height="h-2.5" />
      </div>
      {/* Badge */}
      <div className="mt-2 ml-4">
        <SkeletonLine width="w-14" height="h-4" />
      </div>
    </div>
  )
}

/** Skeleton that matches a card/banner layout */
export function SkeletonCard() {
  return (
    <div className="mx-3 p-3 rounded-xl skeleton-shimmer animate-shimmer">
      <div className="space-y-2">
        <SkeletonLine width="w-3/4" height="h-3" />
        <SkeletonLine width="w-1/2" height="h-2.5" />
      </div>
    </div>
  )
}

/** Skeleton matching a document list row */
export function SkeletonDocumentRow() {
  return (
    <div className="mx-3 py-2.5 flex items-center gap-2.5 animate-fade-in border-b border-neutral-100 dark:border-neutral-800">
      <div className="w-7 h-7 rounded-lg skeleton-shimmer animate-shimmer shrink-0" />
      <div className="flex-1 space-y-1.5">
        <SkeletonLine width="w-3/5" height="h-3" />
        <SkeletonLine width="w-2/5" height="h-2" />
      </div>
      <SkeletonLine width="w-14" height="h-5" />
    </div>
  )
}

/** Skeleton matching a signer card */
export function SkeletonSignerCard() {
  return (
    <div className="rounded-lg p-3 bg-neutral-50 dark:bg-neutral-800/50 animate-fade-in border-l-[3px] border-l-neutral-200 dark:border-l-neutral-700">
      <div className="flex items-center justify-between mb-2">
        <SkeletonLine width="w-16" height="h-2.5" />
      </div>
      <div className="space-y-1.5">
        <SkeletonLine width="w-full" height="h-6" />
        <SkeletonLine width="w-full" height="h-6" />
      </div>
    </div>
  )
}

/** Skeleton for version timeline */
export function SkeletonTimeline() {
  return (
    <div className="px-3 py-2 animate-fade-in">
      <div className="relative ml-2 space-y-2">
        <div className="absolute left-0 top-1 bottom-1 w-px bg-neutral-200 dark:bg-neutral-700" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="relative flex items-center gap-2">
            <div className="relative z-10 w-1.5 h-1.5 rounded-full skeleton-shimmer animate-shimmer shrink-0" style={{ marginLeft: '-3px' }} />
            <SkeletonLine width={i === 1 ? 'w-24' : 'w-16'} height="h-2.5" />
            <span className="flex-1" />
            <SkeletonLine width="w-6" height="h-2" />
          </div>
        ))}
      </div>
    </div>
  )
}
